import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Number of form fields to show
   * @default 5
   */
  fields: p.number(5),

  /**
   * Show submit/action buttons at the bottom
   * @default true
   */
  showButtons: p.boolean(true),

  /**
   * Show helper text below each field
   * @default true
   */
  showHelperText: p.boolean(true),

  /**
   * Compact mode (smaller spacing)
   * @default false
   */
  compact: p.boolean(false),
} satisfies ComponentPropsOptions;

export type FormSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * FormSkeleton - Loading placeholder for form pages
 *
 * Universal skeleton for settings pages, configuration forms, and any vertical
 * form layout. Each field includes label, input, and optional helper text.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <FormSkeleton />
 *
 * // Custom field count
 * <FormSkeleton fields={8} />
 *
 * // Without buttons
 * <FormSkeleton showButtons={false} />
 *
 * // Compact mode
 * <FormSkeleton fields={3} compact />
 *
 * // With QueryLoader
 * <QueryLoader queries={[settingsQuery]}>
 *   <QueryLoader.Loading>
 *     <FormSkeleton fields={6} />
 *   </QueryLoader.Loading>
 *   <form>
 *     ...
 *   </form>
 * </QueryLoader>
 * ```
 */
const FormSkeleton = defineComponent({
  name: "FormSkeleton",
  props,
  setup(props) {
    const spacing = props.compact ? "space-y-4" : "space-y-6";

    return () => (
      <div class={`w-full ${spacing}`}>
        {/* Form fields */}
        {Array.from({ length: props.fields }).map((_, i) => (
          <div key={`field-${i}`} class="space-y-2">
            {/* Label */}
            <Skeleton class="h-4 w-32 rounded" />

            {/* Input field */}
            <Skeleton class="h-10 w-full rounded-md" />

            {/* Helper text (optional) */}
            {props.showHelperText && <Skeleton class="h-3 w-3/4 rounded" />}
          </div>
        ))}

        {/* Action buttons */}
        {props.showButtons && (
          <div class="flex items-center justify-end gap-3 border-t pt-6">
            <Skeleton class="h-10 w-24 rounded-md" />
            <Skeleton class="h-10 w-32 rounded-md" />
          </div>
        )}
      </div>
    );
  },
});

export default FormSkeleton;
